import { Formik } from 'formik';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Col, Grid, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { changeTypeVisitTrackingExtraAPI, discontinuationVisitTrackingExtraAPI, endPatientInProtocolVisitTrackingExtraAPI, exportVisitTrackingAPI, getExtraPendingAttachmentsAPI, getVisitTrackingAttachmentFormEndpoint, getVisitTrackingExtraForms, getVisitTrackingForm, lostToFollowUpVisitTrackingExtraAPI, publishVisitTrackingExtraForm, removeVisitTrackingExtraAPI, returnDrugsVisitTrackingExtraAPI, rollbackVisitStatusExtraAPI, saveUploadVisitTrackingExtraAPI, saveVisitTrackingExtraForm, screenFailureVisitTrackingExtra, uploadVisitTrackingExtraAttachmentFormEndpoint, withoutDrugsVisitTrackingExtraAPI } from '../../../api/protocol';
import { RemoveVisitModal } from '../../../components/Protocols/Tracking/RemoveVisitModal';
import { AuditModal, CreateAuditModal } from '../../../components/AuditTrail/AuditModal';
import { WithAuth } from '../../../components/Authorization/WithAuthProtocol';
import { useAuthProtocol } from '../../../components/Authorization/useAuthorization';
import { DocumentViewer } from '../../../components/CustomControls/DocViewer/DocumentViewer';
import { useFiles } from '../../../components/CustomControls/Files/Hooks';
import { SignWithOutInfo } from '../../../components/DigitalSign/Sign';
import { DynamicForm } from '../../../components/DynamicForm/DynamicForm';
import { MedicalRecord, OnSite, Phone, VideoCall } from '../../../components/Icons/Icons';
import { ModalConfirm } from '../../../components/Modals/ModalConfirm';
import { ModalSpinner } from '../../../components/Modals/ModalSpinner';
import { HandleApiError, HandleError, HandleMessageError, HandleMessagesError, HandleGroupedMessagesError } from '../../../components/Notifications/APIErrorHandler';
import { extractApiErrorCodes } from '../../../components/Utils/extractApiErrorCodes';
import { Success } from '../../../components/Notifications/Notifications';
import { FormActions } from '../../../components/Protocols/ExtraVisits/Forms';
import { PatientStatusInProtocol } from '../../../components/Protocols/Patients';
import { FormMenu, VisitStatus } from '../../../components/Protocols/Tracking/Forms';
import { ChangeVisitType, MissingRequiredFieldsModal } from '../../../components/Protocols/Tracking/Modals';
import { PendingAttachmentsModal } from '../../../components/Protocols/Tracking/PendingAttachmentsModal';
import { Slider } from '../../../components/Slider/Slider';
import { Spinner } from '../../../components/Utils/Loaders';
import { getMissingRequiredFieldsViaSchema, getValidationSchema } from '../../../components/Validations/FormValidationSchema';
import { formStatus, visitStatus, visitStatus as visitStatusEnum, visitTypeEnum } from "../../../variables/Enums";
import { AuditTrail } from "../../Admin/AuditTrail";
import { EntryItemsListTabs } from '../../MedicalRecords/MedicalRecordItems/EntryItemsList';
import { SignatureModal } from '../../Signature/Signature';
import DocumentNotesContainer from '../DocumentNotes/DocumentNotes';
import { EvolutionsContainer } from '../Evolutions/Evolutions';
import { useAudits } from '../Hooks';
import MonitorNotesContainer from '../MonitorNotes/MonitorNotes';
import ProtocolContext from '../ProtocolContext/ProtocolContext';
import { getDuplicateFileNames, getVisitTypeTitle } from '../Tracking/Services';
import EditingUser from '../TrackingForms/EditingUserNotification';
import { useEditingUser } from '../TrackingForms/useEditingUser';
import { MissingConsents } from "../Tracking/VisitTracking/MissingConsents";

export const ExtraVisitTracking = ({ protocolId, patientId, visitId }) => {
  //#region Hooks
  let history = useHistory();
  const [showHistory, setShowHistory] = useState(false);
  const [isAuthorized, isAuthLoading, currentUser] = useAuthProtocol(protocolId);
  const settingsStore = useSelector((state) => state.settings);
  const context = useContext(ProtocolContext);
  const [isAuditLoading, , getLastVisitTrackingChange] = useAudits("VisitTrackingExtra", visitId);
  const [fileData, getBlobAPI, postAPI] = useFiles();
  const { isUserEditing, onCurrentEditorChange, onSelectedFormChange } = useEditingUser();
  const { t } = useTranslation();
  //#endregion

  //#region State
  const [isLoading, setIsLoading] = useState(false);
  const [reload, setReload] = useState(false);
  const [forms, setForms] = useState([]);
  const [signStatus, setSignStatus] = useState(null);
  const [visitTracking, setVisitTracking] = useState(null);
  const [selectedForm, setSelectedForm] = useState(null);
  const [formId, setFormId] = useState(null); // Usado para preseleccionar un Form al hacer un Reload. 
  const [modal, setModal] = useState(null);
  const [patientInfo, setPatientInfo] = useState(null);
  const [evolutions, setEvolutions] = useState([]);
  const [visitType, setVisitType] = useState(null);
  const [missingConsents, setMissingConsents] = useState([]);
  const [warnedFormIds, setWarnedFormIds] = useState(new Set());
  const [pendingMissingFieldsSave, setPendingMissingFieldsSave] = useState(null);
  //#endregion

  const formsWithMissingFields = useMemo(() =>
    (forms || [])
      .filter(f => f.extraFormType !== 0)
      .filter(f => getMissingRequiredFieldsViaSchema(f).length > 0)
      .map(f => ({ id: f.id, title: f.title })),
    [forms]
  );

  const urlParams = new URLSearchParams(window.location.search);
  const returnUrl = urlParams.get("returnUrl");

  const getVisitType = (visitType) => {
    switch (visitType) {
      case visitTypeEnum.OnSite:
        return <OnSite />
      case visitTypeEnum.Telephone:
        return <Phone />
      case visitTypeEnum.Remote:
        return <VideoCall />
      default:
        return "";
    }
  }

  useEffect(() => {
    const fetchData = () => {
      setIsLoading(true);
      setReload(false);

      getVisitTrackingExtraForms(protocolId, visitId, patientId)
        .then((response) => {
          var visitTracking = response.data;

          if (visitTracking.forms && visitTracking.forms.length > 0) {
            let formAux = visitTracking.forms[0];
            if (
              urlParams.has("formId") &&
              visitTracking.forms.find((x) => x.id == urlParams.get("formId"))
            )
              formAux = visitTracking.forms.find(
                (x) => x.id == urlParams.get("formId")
              );

            if (formId && visitTracking.forms.find((x) => x.id == formId))
              formAux = visitTracking.forms.find((x) => x.id == formId);

            formAux.text = visitTracking.visit.text;
            setSelectedForm(formAux);
          }
          setVisitType(visitTracking.visit.visitType)
          setEvolutions(visitTracking.visit.evolutions);
          setSignStatus(visitTracking.signStatus);
          setForms(visitTracking.forms);
          setVisitTracking(visitTracking.visit);
          setPatientInfo({
            patientNumber: visitTracking.patientNumber,
            patientInProtocolStatus: visitTracking.patientInProtocolStatus,
            medicalRecordNumber: visitTracking.medicalRecordNumber,
            fullName: visitTracking.patientFullName
          });
          setMissingConsents(visitTracking.missingConsents);
          setIsLoading(false);
        })
        .catch(error => {
          console.log(error);
          HandleApiError(error);
          setIsLoading(false);
        })
    }

    fetchData();
  }, [protocolId, visitId, patientId, reload]);

  const _closeModal = (formikBag) => {
    if (formikBag) formikBag.setSubmitting(false);
    setModal(null);
  };

  const _updateData = (visit, updatedForm) => {
    if (!updatedForm || !updatedForm.id) {
      window.location.reload(false);
      return;
    }

    updatedForm.text = visit.text;
    let formsCopy = JSON.parse(JSON.stringify(forms));
    var index = formsCopy.findIndex((x) => x.id === updatedForm.id);
    formsCopy.splice(index, 1, updatedForm); //Reemplazo form.
    setVisitTracking(visit);
    setEvolutions(visit.evolutions);
    setForms(formsCopy);
    setSelectedForm(updatedForm);

    _closeModal();
    Success("protocolNotifications.visitTracking_FormSaved");
  };

    //#region SaveVisit
    const _continueWithSave = (formVisitTracking, formikProps) => {
        const duplicates = getDuplicateFileNames(formVisitTracking.fields);
        if (duplicates.length > 0) {
            HandleError(t('protocolErrors.visitTracking_duplicateFileNames', { fileNames: duplicates.join(', ') }), false);
            return;
        }

        if (visitTracking.status !== visitStatusEnum.Started || formVisitTracking.status !== formStatus.Initial) {
            setModal(<CreateAuditModal
                onClose={() => setModal(null)}
                onSave={(reason, comments) => _saveVisit({ patientForm: formVisitTracking, audit: { reason: reason, comments: comments } }, formikProps)}
            />)
        }
        else {
            _saveVisit({ patientForm: formVisitTracking }, formikProps);
        }
    };

    const handleSaveVisit = (formVisitTracking, formikProps) => {
        if (!formikProps.dirty) return; // CU-3: no guardar sin cambios

        // CU-1: advertir si hay campos requeridos incompletos (solo formularios de contenido)
        if (formVisitTracking.extraFormType !== 0) {
            const missingFields = getMissingRequiredFieldsViaSchema(formVisitTracking);
            if (missingFields.length > 0 && !warnedFormIds.has(formVisitTracking.id)) {
                setPendingMissingFieldsSave({ missingFields, formVisitTracking, formikProps });
                return;
            }
        }

        _continueWithSave(formVisitTracking, formikProps);
    };

    const handleConfirmMissingFields = () => {
        const { formVisitTracking, formikProps } = pendingMissingFieldsSave;
        setWarnedFormIds(prev => new Set([...prev, formVisitTracking.id]));
        setPendingMissingFieldsSave(null);
        _continueWithSave(formVisitTracking, formikProps);
    };
    const _saveVisit = async (formVisitTracking, formikProps) => {
        try {
            if (formikProps.isSubmitting)
                return;

      formikProps.setSubmitting(true);
      setModal(<ModalSpinner isShowing={true} hide={null} />);

      let res = await saveVisitTrackingExtraForm(
        protocolId,
        visitId,
        patientId,
        formVisitTracking
      );
      let form = res.data.form;
      const visit = res.data.visit;

        // Visit guardado (200), pero el batch de inventario falló post-save: aviso traducido.
        // inventoryErrors viene camelCase (System.Text.Json): { error, message }.
        const inventoryErrors = res.data.inventoryErrors;
        if (Array.isArray(inventoryErrors) && inventoryErrors.length > 0)
            HandleMessagesError("inventory.errorCodes.title",
                inventoryErrors.map(e => t(`inventory.errorCodes.${e.error}`, { defaultValue: e.message ?? e.error })));


            // Upload attachments if any.
      const fieldsWithFiles = formVisitTracking.patientForm.fields.filter(
        (x) => x._t === "FileUploadField" && x.value.file
      );
      const componentsWithFiles = formVisitTracking.patientForm.fields
        .filter((x) => x._t === "ArrayField")
        .map((array) => {
          return array.components
            .map((component) => {
              return component.fields
                .map(
                  (field) =>
                    field._t === "FileUploadField" && field !== null && field
                )
                .flat();
            })
            .flat();
        })
        .flat()
        .filter((x) => x.value?.file); // filtro por los nuevos solamente.
      const files = fieldsWithFiles.concat(componentsWithFiles);

      if (files && files.length > 0) {
        try {
          const attachs = files.map((x) => x.value);
          postAPI(
            attachs,
            uploadVisitTrackingExtraAttachmentFormEndpoint(
              protocolId,
              visitId,
              form.id,
              patientId
            )
          )
            .then(() => {
              getVisitTrackingForm(protocolId, visitId, form.id)
                .then((resForm) => {
                  // Actualizo el form en FrontEnd.
                  _updateData(visit, resForm?.data);
                })
                .catch((error) => {
                  console.log(error);
                  window.location.reload(false);
                });
            })
            .catch((error) => {
              console.log(error);
            });
        } catch (error) {
          console.log(error.response);
        }
      } else {
        _updateData(visit, form);
      }
    } catch (error) {
      _closeModal();
      console.log(error);
      HandleApiError(error);
    } finally {
      formikProps.setSubmitting(false);
    }
  };
  //#endregion

  //#region Publish Visit
  const _publishVisit = async (digitalSign, formikProps, type) => {

    try {
      setModal(<ModalSpinner isShowing={true} hide={null} />);
      const res = await publishVisitTrackingExtraForm(protocolId, visitId, patientId, digitalSign);
      setModal(null);
      setSelectedForm({ text: res.data.text });
      setVisitTracking(res.data);
      formikProps.setSubmitting(false);
      Success("protocolNotifications.visitTracking_VisitPublished");
      // La visita se publicó, pero la H.C. rechazó la sincronización: se avisa para que re-sincronicen.
      if (res.headers?.["x-sync-failed"] === "true")
        HandleMessageError("protocolNotifications.visitTracking_syncFailed");
    }
    catch (error) {
      setModal(null);
      formikProps.setSubmitting(false);
      console.log(error);
      HandleApiError(error);
    }
  }
  const handleSubmitFormik = async (_formVisitTracking, actions) => {
    const continueToSign = () => {
      if (settingsStore.settings.digitalSignEnabled)
        setModal(<SignatureModal
          isShowing={true}
          hide={() => { setModal(null); actions.setSubmitting(false); }}
          onSubmit={(pin) => _publishVisit(pin, actions)}
        />);
      else
        _publishVisit({ password: "" }, actions);
    };
    try {
      const res = await getExtraPendingAttachmentsAPI(protocolId, visitId, patientId);
      if (res.data.hasPending) {
        setModal(
          <PendingAttachmentsModal
            pendingData={res.data}
            currentUser={currentUser?.sub}
            protocolId={protocolId}
            visitId={visitId}
            patientId={patientId}
            medicalRecordNumber={patientInfo.medicalRecordNumber}
            onSaveUpload={saveUploadVisitTrackingExtraAPI}
            onContinue={() => { setModal(null); continueToSign(); }}
            onCancel={() => { setModal(null); actions.setSubmitting(false); }}
          />
        );
      } else {
        continueToSign();
      }
    } catch (error) {
      HandleApiError(error);
      actions.setSubmitting(false);
    }
  }
  const handleSubmitForm = (evt, formikBag) => {
    evt.preventDefault();

    if (
      !formikBag.dirty ||
      (formikBag.dirty &&
        window.confirm(t("protocolNotifications.visitTracking_unsavedChanges")))
    ) {
      // CU-4: validar campos requeridos en todos los formularios de contenido con detalle de campo
      const formsWithDetails = forms
        .filter(f => f.extraFormType !== 0)
        .reduce((acc, f) => {
          const missingFields = getMissingRequiredFieldsViaSchema(f);
          if (missingFields.length > 0) acc.push({ id: f.id, title: f.title, missingFields });
          return acc;
        }, []);

      if (formsWithDetails.length > 0) {
        const showErrorValidations = formsWithDetails.some(f => f.id === formikBag.values.id);
        HandleGroupedMessagesError("protocolErrors.visitTracking_requiredFieldsError",
          formsWithDetails.map(f => ({ id: f.id, title: f.title, items: f.missingFields.map(mf => mf.title) })));
        if (showErrorValidations)
          formikBag.submitForm();
      } else {
        formikBag.submitForm();
      }
    }
  };
  //#endregion

  //#region SaveUpload
  const _saveUploadVisit = async (formVisitTracking, formikProps) => {
    try {
      if (formikProps.isSubmitting) return;

      formikProps.setSubmitting(true);
      setModal(<ModalSpinner isShowing={true} hide={null} />);

      const res = await saveUploadVisitTrackingExtraAPI(
        protocolId,
        visitId,
        patientId,
        patientInfo.medicalRecordNumber,
        formVisitTracking
      );
      const form = res.data.form;
      const visit = res.data.visit;

        // Visit guardado (200), pero el batch de inventario falló post-save: aviso traducido.
        // inventoryErrors viene camelCase (System.Text.Json): { error, message }.
        const inventoryErrors = res.data.inventoryErrors;
        if (Array.isArray(inventoryErrors) && inventoryErrors.length > 0)
            HandleMessagesError("inventory.errorCodes.title",
                inventoryErrors.map(e => t(`inventory.errorCodes.${e.error}`, { defaultValue: e.message ?? e.error })));

        // Upload attachments.
      const fieldsWithFiles = formVisitTracking.patientForm.fields.filter(
        (x) => x._t === "FileUploadField" && x.value.file
      );
      const componentsWithFiles = formVisitTracking.patientForm.fields
        .filter((x) => x._t === "ArrayField")
        .map((array) => {
          return array.components
            .map((component) => {
              return component.fields
                .map(
                  (field) =>
                    field._t === "FileUploadField" && field !== null && field
                )
                .flat();
            })
            .flat();
        })
        .flat()
        .filter((x) => x.value?.file); // filtro por los nuevos solamente.
      const files = fieldsWithFiles.concat(componentsWithFiles);

            if (files && files.length > 0) {
                try {
                    const attachs = files.map(x => x.value);
                    postAPI(attachs, uploadVisitTrackingExtraAttachmentFormEndpoint(protocolId, visitId, form.id, patientId, patientInfo.medicalRecordNumber))
                        .then(() => {
                            getVisitTrackingForm(protocolId, visitId, form.id)
                                .then(resForm => {
                                    // Actualizo el form en FrontEnd.
                                    _updateData(visit, resForm?.data);
                                }).catch((error) => {
                                    console.log(error)
                                    window.location.reload(false)
                                })
                        }).catch((error) => { console.log(error) });
                }
                catch (error) {
                    console.log(error.response);
                }
            }
            else {
                _updateData(visit, form);
            }
        }
        catch (error) {
            _closeModal();
            console.log(error);
            const codes = extractApiErrorCodes(error);
            if (codes.length > 0)
                HandleMessagesError("inventory.errorCodes.title", codes.map(c => t(`inventory.errorCodes.${c.code}`, { defaultValue: c.message ?? c.code })));
            else
                HandleApiError(error);
        }
    }
    const handleSaveUploadVisit = (formVisitTracking, formikProps) => {
        const duplicates = getDuplicateFileNames(formVisitTracking.fields);
        if (duplicates.length > 0) {
            HandleMessageError(t("protocolErrors.visitTracking_duplicateFileNames", { fileNames: duplicates.join(', ') }));
            return;
        }


        if (visitTracking.status !== visitStatus.Started || formVisitTracking.status !== formStatus.Initial) {
            setModal(<CreateAuditModal
                onClose={() => setModal(null)}
                onSave={(reason, comments) => _saveUploadVisit({ patientForm: formVisitTracking, audit: { reason: reason, comments: comments } }, formikProps)}
            />)
        }
        else {
            _saveUploadVisit({ patientForm: formVisitTracking }, formikProps);
        }
    }
    //#endregion

  const handleAuditTrail = () => {
    setModal(
      <AuditModal
        onClose={() => {
          setModal(null);
        }}
        title={t("auditTrail.audit")}
      >
        {selectedForm && (
          <>
            {t("auditTrail.field")}:
            <AuditTrail
              entity="VisitTrackingExtraForm"
              entityId={selectedForm.id}
              fields={selectedForm && selectedForm.fields}
            />
          </>
        )}
        {t("auditTrail.evolution")}:
        <AuditTrail entity="VisitTrackingExtra" entityId={visitTracking.id} />
      </AuditModal>
    );
  };

  const handleViewFile = async (attachName, field) => {
    try {
      var res = await getBlobAPI(
        attachName,
        getVisitTrackingAttachmentFormEndpoint(
          protocolId,
          visitId,
          selectedForm.id,
          attachName
        )
      );
      if (!res) return;
      setModal(
        <DocumentViewer
          file={res?.blob}
          fileName={res?.fileName}
          isLoading={res?.isLoading}
          userId={field?.validatedBy}
          date={field?.validated}
        />
      );
    } catch (error) {
      console.error(error);
      HandleApiError(error);
    }
  };

  const handleSelectForm = (formId, isDirty) => {
    if (
      !isDirty ||
      (isDirty &&
        window.confirm(t("protocolNotifications.visitTracking_unsavedChanges")))
    ) {
      let selectedForms = forms.filter((x) => {
        return x.id === formId;
      });
      let formAux = selectedForms.length > 0 ? selectedForms[0] : {};
      formAux.text = selectedForm.text;

      setSelectedForm(formAux);

      if (onSelectedFormChange(formId)) {
        setFormId(formId);
        setReload(true);
      } else {
        setFormId(null);
      }
    }
  };

  //#region SceenFailure
  const handleScreenFailure = (formikBag) => {
    if (!formikBag.dirty || (formikBag.dirty && window.confirm(t("protocolNotifications.visitTracking_unsavedChanges")))) {
      setModal(<ModalConfirm
        onCancel={() => _closeModal(formikBag)}
        onConfirm={() => auditScreenFailure(formikBag)}
        title={t(`protocolNotifications.visitTracking_screenFailure_title`)}
        description={t(`protocolNotifications.visitTracking_screenFailure_description`)}
      />)
    }
  }
  const auditScreenFailure = (formikBag) => {
    if (visitTracking.status === visitStatus.Completed || visitTracking.status === visitStatus.Changed)
      setModal(<CreateAuditModal
        onClose={() => _closeModal(formikBag)}
        onSave={(reason, comments) => confirmScreenFailure(formikBag, { reason: reason, comments: comments })}
      />)
    else
      confirmScreenFailure(formikBag, null, { password: "" })
  }
  const confirmScreenFailure = (formikBag, auditTrail) => {
    if (settingsStore.settings.digitalSignEnabled)
      setModal(<SignatureModal
        isShowing={true}
        isLoading={isLoading}
        hide={() => _closeModal(formikBag)}
        onSubmit={(pin) => _publishScreenFailure(formikBag, auditTrail, pin)}
      />);
    else
      _publishScreenFailure(formikBag, auditTrail, { password: "" })
  }
  const _publishScreenFailure = async (formikBag, auditTrail, digitalSign) => {
    try {
      setIsLoading(true);
      await screenFailureVisitTrackingExtra(protocolId, visitId, patientId, { auditTrail, digitalSign });
      Success("protocolNotifications.visitTracking_VisitPublished");
      setReload(true);
    }
    catch (error) {
      console.log(error);
      HandleApiError(error);
    } finally {
      _closeModal(formikBag);
      setIsLoading(false);
    }
  }
  //#endregion

  //#region Discontinuation / EarlyTermination
  const handleDiscontinuation = (formikBag) => {
    if (!formikBag.dirty || (formikBag.dirty && window.confirm(t("protocolNotifications.visitTracking_unsavedChanges")))
    ) {
      setModal(
        <ModalConfirm
          onCancel={() => _closeModal(formikBag)}
          onConfirm={() => creationEarlyTerminationVisit(formikBag)}
          title={t(`protocolNotifications.visitTracking_discontinuation_title`)}
          description={t(
            `protocolNotifications.visitTracking_discontinuation_description`
          )}
        />
      );
    }
  };
  const creationEarlyTerminationVisit = (formikBag) => {
    setModal(<ModalConfirm
      customButtons={<React.Fragment>
        <button className='btn btn-lg btn-default btn-margin' onClick={() => _closeModal(formikBag)}>{t("buttons.cancel")}</button>
        <button className='btn btn-lg btn-info btn-margin' onClick={() => auditDiscontinuation(formikBag, false)}>{t("buttons.no")}</button>
        <button className='btn btn-lg btn-success btn-margin' onClick={() => auditDiscontinuation(formikBag, true)}>{t("buttons.yes")}</button>
      </React.Fragment>}
      title={t(`protocolNotifications.visitTracking_earlyTermination_title`)}
      description={t(`protocolNotifications.visitTracking_earlyTermination_description`)}
    />)
  }
  const auditDiscontinuation = (formikBag, earlyTerminationWillBeCreated) => {
    if (visitTracking.status === visitStatus.Completed || visitTracking.status === visitStatus.Changed)
      setModal(<CreateAuditModal
        onClose={() => _closeModal(formikBag)}
        onSave={(reason, comments) => confirmDiscontinuation(formikBag, earlyTerminationWillBeCreated, { reason: reason, comments: comments })}
      />)
    else
      confirmDiscontinuation(formikBag, earlyTerminationWillBeCreated, null);
  }
  const confirmDiscontinuation = (formikBag, earlyTerminationWillBeCreated, auditTrail) => {
    if (settingsStore.settings.digitalSignEnabled)
      setModal(<SignatureModal
        isShowing={true}
        isLoading={isLoading}
        hide={() => _closeModal(formikBag)}
        onSubmit={(pin) => _publishDiscontinuation(formikBag, earlyTerminationWillBeCreated, auditTrail, pin)}
      />);
    else
      _publishDiscontinuation(formikBag, earlyTerminationWillBeCreated, auditTrail, { password: "" });
  }
  const _publishDiscontinuation = async (formikBag, earlyTerminationWillBeCreated, auditTrail, digitalSign) => {
    try {
      setIsLoading(true);
      await discontinuationVisitTrackingExtraAPI(protocolId, visitId, patientId, earlyTerminationWillBeCreated, { auditTrail, digitalSign });
      Success("protocolNotifications.visitTracking_VisitPublished");
      setReload(true);
    }
    catch (error) {
      console.log(error);
      HandleApiError(error);
    }
    finally {
      _closeModal(formikBag);
      setIsLoading(false);
    }
  }
  //#endregion

  //#region Remove Visit
  const handleRemoveVisit = (formikBag) => {
    setModal(
      <RemoveVisitModal
        onCancel={() => _closeModal(formikBag)}
        onConfirm={(reason) => _confirmRemoveVisit(reason)}
      />
    );
  };
  const _confirmRemoveVisit = async (reason) => {
    try {
      setIsLoading(true);
      _closeModal();
      await removeVisitTrackingExtraAPI(protocolId, visitId, patientId, reason);
      Success("protocolNotifications.visitTracking_removeVisitOk");
      history.push(`/admin/protocols/${protocolId}`);
    } catch (error) {
      console.log(error);
      HandleApiError(error);
    } finally {
      setIsLoading(false);
    }
  };
  //#endregion


  //#region ChangeType
  const handleChangeType = (formikBag) => {
    setModal(<ChangeVisitType
      visit={t("protocolNotifications.visitTracking_changeType_title")}
      onClose={() => _closeModal(formikBag)}
      onChange={(type) => _changeType(type, formikBag)}
    />)
  }
  const _changeType = async (type, formikBag) => {
    try {
      setIsLoading(true);
      const res = await changeTypeVisitTrackingExtraAPI(protocolId, visitId, patientId, type);
      setVisitTracking(res.data);
      Success("protocolNotifications.visitTracking_FormSaved");
    }
    catch (error) {
      console.log(error);
      HandleApiError(error);
    }
    finally {
      _closeModal(formikBag);
      setIsLoading(false);
    }
  }
  //#endregion

  //#region EndPatient 
  const handleEndPatientInProtocol = (formikBag) => {
    if (!formikBag.dirty || (formikBag.dirty && window.confirm(t("protocolNotifications.visitTracking_unsavedChanges")))) {
      setModal(<ModalConfirm
        onCancel={() => _closeModal(formikBag)}
        onConfirm={() => auditEndPatientInProtocol(formikBag)}
        title={t("protocolNotifications.visitTracking_endPatientInProtocol_title")}
        description={t("protocolNotifications.visitTracking_endPatientInProtocol_description")}
      />);
    }
  }
  const auditEndPatientInProtocol = (formikBag,) => {
    if (visitTracking.status === visitStatus.Completed || visitTracking.status === visitStatus.Changed)
      setModal(<CreateAuditModal
        onClose={() => _closeModal(formikBag)}
        onSave={(reason, comments) => confirmEndPatientInProtocol(formikBag, { reason: reason, comments: comments })}
      />)
    else
      confirmScreenFailure(formikBag, null, { password: "" })
  }
  const confirmEndPatientInProtocol = (formikBag, auditTrail) => {
    if (settingsStore.settings.digitalSignEnabled)
      setModal(<SignatureModal
        isShowing={true}
        isLoading={isLoading}
        hide={() => _closeModal(formikBag)}
        onSubmit={(pin) => _publishEndPatientInProtocol(formikBag, auditTrail, pin)}
      />);
    else
      _publishEndPatientInProtocol(formikBag, auditTrail, { password: "" })
  }
  const _publishEndPatientInProtocol = async (formikBag, auditTrail, digitalSign) => {
    try {
      setIsLoading(true);
      await endPatientInProtocolVisitTrackingExtraAPI(protocolId, visitId, patientId, { auditTrail, digitalSign });
      Success("protocolNotifications.visitTracking_FormSaved");
      setReload(true);
    }
    catch (error) {
      console.log(error);
      HandleApiError(error);
    }
    finally {
      _closeModal(formikBag);
      setIsLoading(false);
    }
  }
  //#endregion

  //#region RollBack
  const handleRollbackVisitStatus = (formikBag) => {
    if (
      !formikBag.dirty ||
      (formikBag.dirty &&
        window.confirm(t("protocolNotifications.visitTracking_unsavedChanges")))
    ) {
      setModal(
        <ModalConfirm
          onConfirm={() => confirmRollbackVisitStatus(formikBag)}
          onCancel={() => _closeModal(formikBag)}
          title={t(
            "protocolNotifications.visitTracking_rollbackVisitStatus_title"
          )}
          description={t(
            "protocolNotifications.visitTracking_rollbackVisitStatus_description"
          )}
        />
      );
    }
  };

  const confirmRollbackVisitStatus = (formikBag) => {
    setModal(
      <CreateAuditModal
        onClose={_closeModal(formikBag)}
        onSave={(reason, comments) =>
          _publishRollbackVisitStatus(
            { audit: { reason: reason, comments: comments } },
            formikBag
          )
        }
      />
    );
  };

  const _publishRollbackVisitStatus = async (audit, formikBag) => {
    try {
      setIsLoading(true);
      await rollbackVisitStatusExtraAPI(protocolId, visitId, patientId, audit);
      Success("protocolNotifications.visitTracking_rollbackDone");
      setReload(true);
    } catch (error) {
      console.log(error);
      HandleApiError(error);
    } finally {
      _closeModal(formikBag);
      setIsLoading(false);
    }
  };
  //#endregion

  //#region LostToFollowUp
  const handleLostToFollowUp = (formikBag) => {
    if (
      !formikBag.dirty ||
      (formikBag.dirty &&
        window.confirm(t("protocolNotifications.visitTracking_unsavedChanges")))
    ) {
      setModal(
        <ModalConfirm
          onCancel={() => _closeModal(formikBag)}
          onConfirm={() => confirmLostToFollowUp(formikBag)}
          title={t(`protocolNotifications.visitTracking_lostToFollowUp_title`)}
          description={t(
            `protocolNotifications.visitTracking_lostToFollowUp_description`
          )}
        />
      );
    }
  };
  const confirmLostToFollowUp = (formikBag) => {
    if (settingsStore.settings.digitalSignEnabled)
      setModal(<SignatureModal
        isShowing={true}
        isLoading={isLoading}
        hide={() => _closeModal(formikBag)}
        onSubmit={(pin) => _publishLostToFollowUp(pin, formikBag)}
      />);
    else
      _publishLostToFollowUp({ password: "" }, formikBag)
  }
  const _publishLostToFollowUp = async (digitalSign, formikBag) => {
    try {
      setIsLoading(true);
      await lostToFollowUpVisitTrackingExtraAPI(protocolId, visitId, patientId, { digitalSign });
      Success("protocolNotifications.visitTracking_FormSaved");
      setReload(true);
    }
    catch (error) {
      console.log(error);
      HandleApiError(error);
    }
    finally {
      _closeModal(formikBag);
      setIsLoading(false);
    }
  }
  //#endregion

  //#region WithoutDrugs
  const handleWithoutDrugs = async (formikBag) => {
    setModal(
      <ModalConfirm
        title={t("protocolNotifications.visitTracking_withoutDrugs_title")}
        description={t(
          "protocolNotifications.visitTracking_withoutDrugs_description"
        )}
        onCancel={() => _closeModal(formikBag)}
        onConfirm={() => _setWithoutDrugs(formikBag)}
      />
    );
  };
  const _setWithoutDrugs = async (formikBag) => {
    try {
      setIsLoading(true);
      await withoutDrugsVisitTrackingExtraAPI(protocolId, visitId, patientId);
      Success("protocolNotifications.visitTracking_FormSaved");
      setReload(true);
    } catch (error) {
      console.log(error);
      HandleApiError(error);
    } finally {
      _closeModal(formikBag);
      setIsLoading(false);
    }
  };
  //#endregion

  //#region ReturnDrugs
  const handleReturnDrugs = async (formikBag) => {
    setModal(
      <ModalConfirm
        title={t("protocolNotifications.visitTracking_returnDrugs_title")}
        description={t(
          "protocolNotifications.visitTracking_returnDrugs_description"
        )}
        onCancel={() => _closeModal(formikBag)}
        onConfirm={() => _setReturnDrugs(formikBag)}
      />
    );
  };
  const _setReturnDrugs = async (formikBag) => {
    try {
      setIsLoading(true);
      await returnDrugsVisitTrackingExtraAPI(protocolId, visitId, patientId);
      Success("protocolNotifications.visitTracking_FormSaved");
      setReload(true);
    } catch (error) {
      console.log(error);
      HandleApiError(error);
    } finally {
      _closeModal(formikBag);
      setIsLoading(false);
    }
  };
  //#endregion
  const _export = async (pin) => {
    try {
      setModal(<ModalSpinner isShowing={true} hide={null} />);

      const response = await exportVisitTrackingAPI(
        protocolId,
        visitTracking?.id,
        patientId,
        pin?.password
      );
      //const blob = new Blob([response.data], { type: "text/html" });
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.target = "_blank";

      var fileName = "PatientVisit.zip";
      var disposition = response.headers['content-disposition'];
      if (disposition && disposition.indexOf('attachment') !== -1) {
        var filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        var matches = filenameRegex.exec(disposition);
        if (matches != null && matches[1]) fileName = matches[1].replace(/['"]/g, '');
      }
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      _closeModal();
    }
    catch (error) {
      setIsLoading(false);
      _closeModal();
      console.log(error);
      HandleApiError(error);
    }

  }
  const handleExport = async () => {
    if (settingsStore.settings.digitalSignEnabled)
      setModal(<SignatureModal
        isShowing={true}
        hide={_closeModal}
        onSubmit={(pin) => _export(pin)}
      />);

    else
      _export("")
  }

  const handleBack = (evt) => {
    evt.preventDefault();
    if (returnUrl) {
      history.push(decodeURIComponent(returnUrl));
    } else {
      history.push(`/admin/protocols/${protocolId}/section/visittracking`);
    }
  };

  const handleCurrentEditorChange = (editor) => {
    setReload(onCurrentEditorChange(editor, selectedForm?.id));
  };

  if (!visitTracking || isLoading || isAuditLoading || isAuthLoading)
    return <Spinner />;

  const isDisabled =
    !isAuthorized(["VisitTracking.Add"]) || context.isDisabled || isUserEditing;
  const isUploadEnabled =
    isAuthorized(["VisitTracking.UploadFile"]) &&
    !context.isDisabled &&
    !isUserEditing;

  return (
    <>
      {modal}
      {pendingMissingFieldsSave && (
        <MissingRequiredFieldsModal
          missingFields={pendingMissingFieldsSave.missingFields}
          onConfirm={handleConfirmMissingFields}
          onCancel={() => setPendingMissingFieldsSave(null)}
        />
      )}
      <Formik
        initialValues={selectedForm}
        validationSchema={getValidationSchema}
        onSubmit={handleSubmitFormik}
        enableReinitialize={true}
        validateOnBlur={false}
      >
        {props => (
          <form onSubmit={(evt) => handleSubmitForm(evt, props)}>
            <Row>
              <Col md={12}>
                <EditingUser currentUser={currentUser} currentFormId={selectedForm?.id} isDirty={props.dirty} onCurrentEditorChange={handleCurrentEditorChange} />
              </Col>
            </Row>
            <MissingConsents
              protocolId={protocolId}
              patientId={patientId}
              missingConsents={missingConsents}
              setMissingConsents={setMissingConsents}
            />
            <Row>
              <Col md={4} lg={3}>
                <Row>
                  <Col md={12}>
                    <FormMenu
                      forms={forms}
                      onClick={(formId) => handleSelectForm(formId, props.dirty)}
                      selectedFormId={selectedForm?.id}
                      formsWithErrors={formsWithMissingFields}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={12} className="sticky">
                    <FormActions
                      protocolId={protocolId}
                      visit={visitTracking}
                      forms={forms}
                      patientInProtocolStatus={patientInfo?.patientInProtocolStatus}
                      //onBack={(evt) => { evt.preventDefault(); history.goBack(); }}
                      onBack={handleBack}
                      onSave={() => handleSaveVisit(props.values, props)}
                      onSaveUpload={() => handleSaveUploadVisit(props.values, props)}
                      onAuditTrail={handleAuditTrail}
                      onScreenFailure={() => handleScreenFailure(props)}
                      onLostToFollowUp={() => handleLostToFollowUp(props)}
                      onDiscontinuation={() => handleDiscontinuation(props)}
                      onEndPatientInProtocol={() => handleEndPatientInProtocol(props)}
                      onRollbackVisitStatus={() => handleRollbackVisitStatus(props)}
                      onWithoutDrugs={() => handleWithoutDrugs(props)}
                      onReturnDrugs={() => handleReturnDrugs(props)}
                      onBackToMR={() => history.push(`/admin/medicalRecords/${visitTracking.patientId}`)}
                      onRemove={() => handleRemoveVisit(props)}
                      onExport={handleExport}
                      isSubmitting={props.isSubmitting}
                      disabled={isUserEditing}
                      hasMissingConsents={missingConsents?.length > 0}
                      disabledSave={!props.dirty}
                      onChangeType={() => handleChangeType(props)}
                    />
                  </Col>
                </Row>
              </Col>
              <Col md={4} lg={5}>
                {visitTracking && <VisitStatus status={visitTracking?.status} date={visitTracking?.created} />}
                <DynamicForm
                  {...props}
                  currentUser={currentUser?.sub}
                  patientId={visitTracking.patientId}
                  patientNumber={patientInfo?.patientNumber}
                  visitId={visitId}
                  protocolId={protocolId}
                  onDownload={handleViewFile}
                  isDisabled={isDisabled}
                  isUploadEnabled={isUploadEnabled}
                  getLastChange={getLastVisitTrackingChange}
                />
                <WithAuth protocolId={protocolId} requiredPermission={["InternalNotes.List"]}>
                  {
                    <DocumentNotesContainer
                      protocolId={protocolId}
                      documentId={selectedForm?.id}
                      visitId={visitId}
                      patientId={patientInfo?.patientNumber}
                      type="VisitTrackingExtra"
                      noteId={urlParams.get("noteId")}
                    />
                  }
                </WithAuth>
              </Col>
              <Col md={4} lg={4} className="sticky">
                <button type="button"
                  className="btn btn-primary btn-fill btn-full"
                  onClick={() => { setShowHistory(!showHistory) }}
                  style={{ marginBottom: '22px', textAlign: 'center' }}>
                  <MedicalRecord /> {t("protocols.visitTracking.viewMR")}
                </button>
                <EvolutionsContainer
                  visitType="VisitTrackingExtra"
                  medicalRecordNumber={patientInfo.medicalRecordNumber}
                  protocolId={protocolId}
                  visitId={visitId}
                  patientId={patientId}
                  visitTrackingStatus={visitTracking.status}
                  setVisitTracking={setVisitTracking}
                  data={evolutions}
                  {...props}
                  audit={getLastVisitTrackingChange("VisitText")} //Text
                  isDisabled={isDisabled}
                  title={
                    <>
                      {getVisitType(visitTracking.visitType)}
                      {getVisitTypeTitle(visitTracking.type)}
                    </>
                  }
                  subtitle={<><PatientStatusInProtocol key="statusrow" status={patientInfo?.patientInProtocolStatus} /> {patientInfo?.patientNumber} {patientInfo?.fullName}</>}
                />
                {
                  (visitTracking?.status === visitStatusEnum.Completed || visitTracking?.status === visitStatusEnum.Skipped || visitTracking?.status === visitStatusEnum.Failed) &&
                  <SignWithOutInfo signedBy={visitTracking.createdById} created={visitTracking.created} locale={true} />
                }
                {
                  /*settingsStore.settings.digitalSignEnabled &&
                  <DigitalSign signedBy={visitTracking.digitalSign?.signedBy} signStatus={signStatus} signedDate={visitTracking.digitalSign?.signedDate} />*/
                }
                <WithAuth protocolId={protocolId} requiredPermission={["ExternalNotes.List"]}>
                  {
                    <MonitorNotesContainer
                      protocolId={protocolId}
                      documentId={selectedForm?.id}
                      visitId={visitId}
                      patientId={patientInfo?.patientNumber}
                      type="VisitTrackingExtra"
                    />
                  }
                </WithAuth>
              </Col>
              <Slider isOpen={showHistory}>
                <Grid fluid>
                  <button type="button"
                    className="btn btn-primary btn-fill btn-full"
                    onClick={() => { setShowHistory(!showHistory) }}
                    style={{ margin: '22px 0', textAlign: 'center' }}>
                    <MedicalRecord /> {t("protocols.visitTracking.hideMR")}
                  </button>
                  <EntryItemsListTabs medicalRecordNumber={visitTracking.patientId} inProtocol />
                </Grid>
              </Slider>
            </Row>
          </form>
        )}
      </Formik>
    </>
  );
}